import type { VoidFunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import { stringify } from 'qs';
import type { SetOptional } from 'type-fest';
import { RouterLink } from '../../generic/Link';
import { icons, IconText } from '../../generic/Icon';
import * as IssuesV2Store from '../../../redux/issuesV2';
import styles from './IssueDetails.module.less';
import type { HydrateStateQuery } from '../../../redux/issuesV2/types';

/**
 * Function which takes the current pathname and removes everything
 * after the last `/`. This works because the routes for the issue
 * details is always just `/{...IssuesInboxPath...}/:id`, so this
 * will strip the `:id` part.
 *
 * @param {string} pathname - From window.location.pathname.
 * @example
 * stripLastPathSegment('/issues/quality/123'); // => `/issues/quality`
 * stripLastPathSegment('/projects/:projectId/refs/branch/main/issues/quality/123'); // => `/projects/:projectId/refs/branch/main/issues/quality`
 */
const stripLastPathSegment = (pathname: string): string => pathname
  .split('/') // Split into array of path parts.
  .slice(0, -1) // Remove the last part of the path.
  .join('/'); // Join them back to a string.

/**
 * BackToIssues is a link which will bring the user back to the issues page.
 * It checks the Redux store to check if there are filters which it can
 * stringify so that the user is linking back with the same filters applied.
 *
 * Previously, we were calling `router.goBack` (same as clicking the back button in the browser),
 * which doesn't work as expected if the user lands directly on the issue details/aren't coming
 * from the issue details page.
 */
const BackToIssues: VoidFunctionComponent = () => {
  const filters: SetOptional<HydrateStateQuery, 'issueCategory'> = useSelector(
    IssuesV2Store.selectors.getHydrateStateQuery,
  );
  delete filters.issueCategory;
  const basePath = stripLastPathSegment(window.location.pathname);
  const backToURL = `${basePath}?${stringify(filters)}`;
  return (
    <div className={styles.backToLink}>
      <RouterLink to={backToURL}>
        <IconText
          icon={icons.angleLeft}
          text="Back to issues"
        />
      </RouterLink>
    </div>
  );
};

export default BackToIssues;
